import React, { useState, useMemo } from 'react';

interface MiniCalendarWidgetProps {
  data: any;
  config: Record<string, any>;
  loading: boolean;
  error: string | null;
  width: number;
  height: number;
}

const WEEKDAYS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sab'];
const MONTHS = ['Janeiro', 'Fevereiro', 'Marco', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];

const MiniCalendarWidget: React.FC<MiniCalendarWidgetProps> = ({ data, loading, error }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const today = new Date();

  // Map activities to dates
  const activityDates = useMemo(() => {
    const dates = new Map<string, number>();
    if (!Array.isArray(data)) return dates;
    data.forEach((activity: any) => {
      const dateStr = (activity.scheduled_at || activity.start_date || activity.created_at || '').slice(0, 10);
      if (dateStr) dates.set(dateStr, (dates.get(dateStr) || 0) + 1);
    });
    return dates;
  }, [data]);

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const firstDayOfMonth = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const prevMonth = () => setCurrentDate(new Date(year, month - 1, 1));
  const nextMonth = () => setCurrentDate(new Date(year, month + 1, 1));

  const days: (number | null)[] = [];
  for (let i = 0; i < firstDayOfMonth; i++) days.push(null);
  for (let d = 1; d <= daysInMonth; d++) days.push(d);

  if (loading) {
    return (
      <CalendarShell year={year} month={month} onPrev={prevMonth} onNext={nextMonth}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 'var(--space-8)' }}>
          <div style={{ width: '24px', height: '24px', border: '3px solid var(--border-light)', borderTopColor: 'var(--color-primary-600)', borderRadius: '50%', animation: 'spin 0.6s linear infinite' }} />
        </div>
      </CalendarShell>
    );
  }

  if (error) {
    return (
      <CalendarShell year={year} month={month} onPrev={prevMonth} onNext={nextMonth}>
        <div style={{ textAlign: 'center', padding: 'var(--space-6)', color: 'var(--text-tertiary)', fontSize: 'var(--text-sm)' }}>
          {error}
        </div>
      </CalendarShell>
    );
  }

  return (
    <CalendarShell year={year} month={month} onPrev={prevMonth} onNext={nextMonth}>
      {/* Weekday headers */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '2px', padding: '0 var(--space-3)', marginBottom: 'var(--space-1)' }}>
        {WEEKDAYS.map((d) => (
          <div key={d} style={{ textAlign: 'center', fontSize: '10px', fontWeight: 600, color: 'var(--text-tertiary)', padding: '2px 0', textTransform: 'uppercase' }}>
            {d}
          </div>
        ))}
      </div>

      {/* Days grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '2px', padding: '0 var(--space-3)' }}>
        {days.map((day, idx) => {
          if (day === null) return <div key={`empty-${idx}`} />;

          const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
          const count = activityDates.get(dateStr) || 0;
          const isToday = day === today.getDate() && month === today.getMonth() && year === today.getFullYear();

          return (
            <div
              key={day}
              style={{
                textAlign: 'center',
                padding: '4px 2px',
                borderRadius: 'var(--radius-md)',
                fontSize: '12px',
                fontWeight: isToday ? 700 : 400,
                color: isToday ? 'white' : 'var(--text-primary)',
                background: isToday ? 'var(--color-primary-600)' : 'transparent',
                position: 'relative',
                cursor: count > 0 ? 'pointer' : 'default',
              }}
              title={count > 0 ? `${count} atividade${count > 1 ? 's' : ''}` : undefined}
            >
              {day}
              {count > 0 && (
                <div style={{
                  position: 'absolute',
                  bottom: '1px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  display: 'flex',
                  gap: '1px',
                }}>
                  {Array.from({ length: Math.min(count, 3) }).map((_, i) => (
                    <div key={i} style={{
                      width: '3px', height: '3px', borderRadius: '50%',
                      background: isToday ? 'rgba(255,255,255,0.8)' : '#6366f1',
                    }} />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </CalendarShell>
  );
};

function CalendarShell({ year, month, onPrev, onNext, children }: {
  year: number; month: number; onPrev: () => void; onNext: () => void; children: React.ReactNode;
}) {
  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', padding: 'var(--space-4)' }}>
      {/* Header with month navigation */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--space-3)' }}>
        <button onClick={onPrev} style={navBtnStyle}>‹</button>
        <h3 style={{ fontSize: 'var(--text-base)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--text-primary)', margin: 0 }}>
          {MONTHS[month]} {year}
        </h3>
        <button onClick={onNext} style={navBtnStyle}>›</button>
      </div>
      <div style={{ flex: 1, minHeight: 0 }}>{children}</div>
    </div>
  );
}

const navBtnStyle: React.CSSProperties = {
  background: 'transparent', border: '1px solid var(--glass-border)',
  borderRadius: 'var(--radius-md)', width: '28px', height: '28px',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  cursor: 'pointer', color: 'var(--text-secondary)', fontSize: '16px', fontWeight: 600,
};

export default MiniCalendarWidget;
